import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { CameraFlyThroughControls, CameraTarget } from '@strava/mre';

import { PLAY_PAUSE_ICON_DISPLAY_TIME } from '../../constants';
import { getWorldPoint3ArrayFromLatLngElevationArray } from '../../helpers/getWorldPoint3ArrayFromLatLngElevationArray';
import { useMRE } from '../../hooks/useMRE';
import {
  RenderedFlyoverContext,
  RenderedFlyoverPlaybackState
} from '../../hooks/useRenderedFlyoverState';
import { FlyoverSpeedControl } from '../FlyoverSpeedControl/FlyoverSpeedControl';
import { PlayPauseIcon } from '../PlayPauseIcon/PlayPauseIcon';
export interface FlyoverRendererProps {
  playbackSpeedFactor: number;
  path: Array<[number, number, number]>; // lat, lng, elevation
  disabled?: boolean;
  onProgressChanged?: (progress: number) => void;
  endTarget?: CameraTarget;
}

export const FlyoverRenderer = ({
  playbackSpeedFactor,
  path,
  disabled,
  onProgressChanged,
  endTarget
}: FlyoverRendererProps) => {
  const { terrainEngine, track } = useMRE();

  // Relied upon within useEffects, but we do not want a value update to trigger re-run of useEffects.
  const playbackSpeedMultiplier = useRef(1);

  const [showPlayPauseIcon, setShowPlayPauseIcon] = useState(false);

  const [flyoverHandle, setFlyoverHandle] =
    useState<CameraFlyThroughControls>();

  const [playbackState, setPlaybackState] =
    useState<RenderedFlyoverPlaybackState>('playing'); // for now we only support starting with auto-play

  const [progress, setProgress] = useState<number>(0);

  const startFlyover = useCallback(
    (
      options: { paused: boolean; progress: number } = {
        paused: false,
        progress: 0
      }
    ) => {
      if (!terrainEngine || !path || !playbackSpeedFactor) {
        return;
      }

      setPlaybackState(options.paused ? 'paused' : 'playing');

      // We filter duplicate points out of the the flyover track before passing it to MRE
      const track = path.filter((curr, index) => {
        if (index === 0) {
          return true;
        }

        // cast to prevent compiler "possibly undefined" error, which we know can't be the case based on index === 0
        // check above
        const last = path[index - 1] as [number, number, number];

        return last[0] !== curr[0] || last[1] !== curr[1];
      });

      // If endTarget is provided, use it to set the camera target at the end of the flyover
      // Otherwise, set to undefined
      const cameraEndTarget = endTarget
        ? {
            avoidMountains: false,
            cameraTarget: endTarget
          }
        : undefined;

      const newHandle = terrainEngine.getCamera().flyover(
        true, // "isInteractive" - enables detachable camera, but is also required for speed control etc.
        getWorldPoint3ArrayFromLatLngElevationArray(track),
        { top: 0, right: 0, bottom: 0.1, left: 0 },
        {
          onFlyThroughProgress: (_, trackProgress: number) => {
            setProgress(trackProgress);
          },
          onInterrupted: () => {
            /*
             * Docs:
             * Called if the flyThrough was interrupted before the animation finished (or was explicitly stopped)
             * with CameraFlyThroughControls#stop. Will be also immediately called if the flyThrough(Interactive)
             * method received bad data (line with less than 2 points or no matching styles found).
             *
             * Reaching here does not indicate an error.
             */
          }
        },

        cameraEndTarget,
        undefined,
        undefined
      );

      if (newHandle) {
        newHandle.setAnimationSpeedMultiplier(
          options.paused
            ? 0
            : playbackSpeedFactor * playbackSpeedMultiplier.current
        );

        if (options.progress) {
          newHandle.setTrackProgress(options.progress);
        }

        setFlyoverHandle(newHandle);
      }
    },
    [endTarget, terrainEngine, path, playbackSpeedFactor]
  );

  const handleShowPlayPauseIcon = () => {
    setShowPlayPauseIcon(true);

    setTimeout(() => {
      setShowPlayPauseIcon(false);
    }, PLAY_PAUSE_ICON_DISPLAY_TIME);
  };

  const handlePause = useCallback(() => {
    if (disabled) {
      return;
    }

    setPlaybackState('paused');
    handleShowPlayPauseIcon();
    flyoverHandle?.setAnimationSpeedMultiplier(0);

    track({
      action: 'pause',
      element: 'flyover'
    });
  }, [disabled, flyoverHandle, track]);

  const handleResume = useCallback(() => {
    if (disabled) {
      return;
    }
    setPlaybackState('playing');
    handleShowPlayPauseIcon();
    flyoverHandle?.setAnimationSpeedMultiplier(
      playbackSpeedFactor * playbackSpeedMultiplier.current
    );
    track({
      action: 'resume',
      element: 'flyover'
    });
  }, [disabled, flyoverHandle, playbackSpeedFactor, track]);

  const handleRestart = useCallback(() => {
    if (disabled) {
      return;
    }

    startFlyover();

    track({
      action: 'restart',
      element: 'flyover'
    });
  }, [disabled, startFlyover, track]);

  const setPlaybackSpeedMultiplier = (value: number) => {
    playbackSpeedMultiplier.current = value;

    if (playbackState === 'playing') {
      flyoverHandle?.setAnimationSpeedMultiplier(playbackSpeedFactor * value);

      track({
        action: 'set_playback_speed_multiplier',
        element: 'flyover',
        properties: {
          playbackSpeedMultiplier: value
        }
      });
    } else {
      // do nothing - handleResume will be responsible for updating the engine
    }
  };

  const handleExternalSetProgress = (progress: number) => {
    // If updating progress from a 'finished' state, we want to pause the flyover
    if (playbackState === 'finished') {
      handlePause();
    }

    flyoverHandle?.setTrackProgress(progress);
  };

  // Autoplay
  useEffect(() => {
    startFlyover();
  }, [startFlyover]);

  // Stop animation on unmount
  useEffect(() => {
    return () => {
      // on unmount, we want to for sure make sure the flyover is finished
      flyoverHandle?.stop();
    };
  }, [flyoverHandle]);

  // track progress updates
  useEffect(() => {
    progress && onProgressChanged?.(progress);

    if (progress === 1) {
      setPlaybackState('finished');
    }
  }, [progress, onProgressChanged]);

  // Pause animation when `disabled`
  useEffect(() => {
    if (disabled) {
      flyoverHandle?.setAnimationSpeedMultiplier(0); // effectively pause the animation
    }
  }, [disabled, flyoverHandle]);

  // Setup keyboard event listeners
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.code === 'Space') {
        e.preventDefault();

        if (playbackState === 'playing') {
          handlePause();
        } else if (playbackState === 'finished') {
          handleRestart();
        } else {
          handleResume();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handlePause, handleRestart, handleResume, playbackState]);

  return (
    <RenderedFlyoverContext.Provider
      value={{
        playbackState,
        playbackSpeedMultiplier: playbackSpeedMultiplier.current,
        setPlaybackSpeedMultiplier,
        progress,
        setProgress: handleExternalSetProgress,
        pause: handlePause,
        resume: handleResume,
        restart: handleRestart
      }}
    >
      <>
        {/* Note: If the DOM structure changes here, this selector may need to be
      updated - https://github.com/strava/web/blob/main/apps/core/pages/activities/%5Bid%5D/flyover.module.scss#L22 */}
        {!disabled && <FlyoverSpeedControl />}
        {showPlayPauseIcon && <PlayPauseIcon playbackState={playbackState} />}
      </>
    </RenderedFlyoverContext.Provider>
  );
};
